import { useState, useCallback, useRef, useEffect } from 'react';

interface ResizableResult {
    viewerWidth: number;
    isResizing: boolean;
    elementRef: React.RefObject<HTMLDivElement>;
    handleResizeStart: (e: React.MouseEvent) => void;
}

/**
 * Custom hook to handle resizable element behavior
 * @param initialWidth - Initial width in viewport width units (vw)
 * @param isMinimized - Whether the component is minimized
 * @param maximizedPosition - Position of the component ('left' or 'right')
 * @returns - Resize handlers and state
 */
export const useResizable = (
    initialWidth = 40,
    isMinimized: boolean,
    maximizedPosition: 'left' | 'right',
): ResizableResult => {
    const [viewerWidth, setViewerWidth] = useState<number>(initialWidth);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [resizeStartX, setResizeStartX] = useState<number>(0);
    const [initialResizeWidth, setInitialResizeWidth] = useState<number>(0);
    const elementRef = useRef<HTMLDivElement>(null);

    // Start resizing the element
    const handleResizeStart = useCallback(
        (e: React.MouseEvent) => {
            if (isMinimized) return;

            e.preventDefault();
            e.stopPropagation();

            setIsResizing(true);
            setResizeStartX(e.clientX);

            // Get the initial width from the DOM element
            const element = elementRef.current;
            if (element) {
                setInitialResizeWidth(element.getBoundingClientRect().width);
            }
        },
        [isMinimized],
    );

    // Handle resizing movement
    const handleResizeMove = useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;

            // Calculate new width based on resize direction
            let newWidth;
            const windowWidth = window.innerWidth;
            const deltaX = e.clientX - resizeStartX;

            if (maximizedPosition === 'right') {
                // When on right side, moving left increases width
                newWidth = initialResizeWidth - deltaX;
            } else {
                // When on left side, moving right increases width
                newWidth = initialResizeWidth + deltaX;
            }

            // Set minimum width to 400px and maximum to 95% of viewport width
            const minWidthPx = 400;
            const maxWidthPx = windowWidth * 0.95;

            // Constrain between min and max in pixels first
            const constrainedWidthPx = Math.min(
                maxWidthPx,
                Math.max(minWidthPx, newWidth),
            );

            // Convert to vw for responsive behavior
            const newWidthVw = (constrainedWidthPx / windowWidth) * 100;

            setViewerWidth(newWidthVw);
        },
        [isResizing, initialResizeWidth, resizeStartX, maximizedPosition],
    );

    // End resizing
    const handleResizeEnd = useCallback(() => {
        if (isResizing) {
            setIsResizing(false);
        }
    }, [isResizing]);

    // Add and remove event listeners for resize operations
    useEffect(() => {
        if (!isMinimized) {
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', handleResizeEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
        };
    }, [isMinimized, handleResizeMove, handleResizeEnd]);

    return {
        viewerWidth,
        isResizing,
        elementRef,
        handleResizeStart,
    };
};
